import React, { useState } from 'react';
import { 
  Building2, Users, BookOpen, Layers, CheckCircle2, Clock, 
  AlertCircle, ShieldCheck, FileText, Video, Plus, Eye, 
  FolderCheck, Filter, Search, Award, Sparkles, Check, X,
  GraduationCap, ChevronRight, Lock, Key
} from 'lucide-react';
import { LMS_MASTER_CURRICULUM } from '../../data/lmsAuthData';

export const GlobalContentManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('curriculum');
  const [curriculum, setCurriculum] = useState(LMS_MASTER_CURRICULUM);
  const [selectedCourseCode, setSelectedCourseCode] = useState('DBA 911');
  const [searchQuery, setSearchQuery] = useState('');

  // Audit queue for materials submitted by faculty
  const [auditQueue, setAuditQueue] = useState([
    {
      id: "AUDIT-2026-091",
      faculty: "Prof. Asuquo E. Johnson",
      programme: "DBA",
      course: "DBA 911",
      targetCohort: "2025/2026 Executive Intake",
      module: "Module 1: Corporate Turnaround & Crisis Diagnostics",
      componentSlot: "Executive Lecture Slides (PDF/PPT)",
      fileName: "DBA911_Mod1_Slides_v2.pdf",
      size: "4.8 MB",
      date: "18 Oct 2026",
      status: "Approved & Live",
      verifiedAudience: "6 Cleared Candidates"
    },
    {
      id: "AUDIT-2026-092",
      faculty: "Prof. Asuquo E. Johnson",
      programme: "DBA",
      course: "DBA 911",
      targetCohort: "2025/2026 Executive Intake",
      module: "Module 2: Strategic Value Innovation",
      componentSlot: "Harvard / Industry Case Study",
      fileName: "Value_Innovation_West_Africa_Case.pdf",
      size: "3.4 MB",
      date: "17 Oct 2026",
      status: "Approved & Live",
      verifiedAudience: "6 Cleared Candidates"
    },
    {
      id: "AUDIT-2026-093",
      faculty: "Prof. N. A. Udoka",
      programme: "PhD BA",
      course: "PHD 901",
      targetCohort: "2025/2026 Doctoral Research Fellows",
      module: "Module 1: PLS-SEM Research Methodology",
      componentSlot: "Empirical Stata / R Dataset",
      fileName: "Industrial_Survey_Wave1.dta",
      size: "18.5 MB",
      date: "18 Oct 2026",
      status: "Pending QA Clearance",
      verifiedAudience: "Idorenyin Bassey (PhD)"
    }
  ]);

  // Cohort Student Clearance Access Roster (Reflecting the 6 students)
  const cohortAccessRoster = [
    { name: "Emem Udoh", matric: "AKSU/BS/2026/DBA/012", prog: "DBA", clearance: "Acceptance Cleared", accessScope: "Full DBA 911 Module Access", status: "Active" },
    { name: "Idorenyin Bassey", matric: "AKSU/BS/2026/PHD/004", prog: "PhD", clearance: "Session Tuition (50%)", accessScope: "Full PHD 901 & Colloquia Access", status: "Active" },
    { name: "Aniefiok Okon", matric: "AKSU/BS/2026/MBA/089", prog: "MBA", clearance: "Session Tuition (Full)", accessScope: "Full MGT 801 & MBA Core", status: "Active" },
    { name: "Obong Victor Okon", matric: "AKSU/BS/2026/APP/101", prog: "DBA", clearance: "Application Fee Paid", accessScope: "Screening & Orientation Syllabus Only", status: "Provisional" },
    { name: "Blessing Eno Archibong", matric: "AKSU/BS/2026/APP/102", prog: "EMAF", clearance: "Application Fee Paid", accessScope: "Screening & Finance Overview Only", status: "Provisional" },
    { name: "Kufre Mfoniso James", matric: "AKSU/BS/2026/APP/103", prog: "PGD", clearance: "Application Fee Paid", accessScope: "Screening & Bridging Syllabus Only", status: "Provisional" }
  ];

  const handleApproveMaterial = (id) => {
    setAuditQueue(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, status: "Approved & Live" };
      }
      return item;
    }));
  };

  const selectedCourse = curriculum.find(c => c.courseCode === selectedCourseCode) || curriculum[0];

  return (
    <div className="space-y-8">
      
      {/* Global Content Manager Banner */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-2xl p-6 sm:p-8 border-2 border-[#7A470E] shadow-xl flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop" 
              alt="LMS Global Content Manager" 
              className="w-16 h-16 rounded-full object-cover border-2 border-[#7A470E] shadow-md"
            />
            <span className="absolute bottom-0 right-0 bg-emerald-500 w-4 h-4 rounded-full border-2 border-[#0B2644]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-xs uppercase font-black tracking-widest text-[#B87A28] font-sc">
                LMS Global Content Administration
              </span>
              <span className="text-[10px] bg-emerald-950 text-emerald-300 font-mono font-bold px-2 py-0.5 rounded border border-emerald-500/40">
                GLOBAL ADMIN ACCESS
              </span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-sc text-white mt-0.5">
              Engr. Dr Inemesit Udofia, <span className="font-sans text-xs font-normal text-[#B87A28]">PhD, FNCS, MCPN</span>
            </h2>
            <div className="text-xs text-slate-300 mt-0.5">
              Chief Academic Technology Officer & Global LMS Administrator • AKSU Business School
            </div>
          </div>
        </div>

        {/* Global Administrative KPIs */}
        <div className="flex items-center space-x-3 text-xs">
          <div className="bg-[#030F1C] px-4 py-2 rounded-xl border border-slate-800 text-right">
            <span className="text-slate-400 block text-[10px] uppercase font-bold font-sc">Programmes Managed</span>
            <span className="text-xl font-black text-[#B87A28] font-sc">6 Programmes</span>
          </div>
          <div className="bg-[#06182B] px-4 py-2 rounded-xl border border-[#7A470E]/40 text-right">
            <span className="text-slate-400 block text-[10px] uppercase font-bold font-sc">Live Student Roster</span>
            <span className="text-xl font-black text-[#B87A28] font-sc">6 Candidates</span>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs for Global LMS Administration */}
      <div className="bg-white rounded-2xl p-2 border-2 border-slate-200 shadow-sm flex flex-wrap gap-2 text-xs font-sc">
        <button
          onClick={() => setActiveTab('curriculum')}
          className={`px-4 py-2.5 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-2 ${
            activeTab === 'curriculum'
              ? 'bg-[#0B2644] text-white shadow-xs'
              : 'text-slate-700 hover:bg-slate-100 hover:text-[#0B2644]'
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Curriculum & Module Taxonomy Master</span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2.5 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-2 ${
            activeTab === 'audit'
              ? 'bg-[#0B2644] text-white shadow-xs'
              : 'text-slate-700 hover:bg-slate-100 hover:text-[#0B2644]'
          }`}
        >
          <FolderCheck className="w-4 h-4" />
          <span>Faculty Upload Audit Queue ({auditQueue.filter(q => q.status.includes('Pending')).length})</span>
        </button>

        <button
          onClick={() => setActiveTab('students')}
          className={`px-4 py-2.5 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-2 ${
            activeTab === 'students'
              ? 'bg-[#0B2644] text-white shadow-xs'
              : 'text-slate-700 hover:bg-slate-100 hover:text-[#0B2644]'
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          <span>Cohort Access & Clearance Gates (6 Students)</span>
        </button>

        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2.5 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-2 ${
            activeTab === 'security'
              ? 'bg-[#0B2644] text-white shadow-xs'
              : 'text-slate-700 hover:bg-slate-100 hover:text-[#0B2644]'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>Least Privilege & RBAC Matrix</span>
        </button>
      </div>

      {/* TAB 1: CURRICULUM & MODULE TAXONOMY MASTER */}
      {activeTab === 'curriculum' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left: Course Selection Sidebar */}
          <div className="lg:col-span-4 bg-white rounded-2xl border-2 border-slate-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc">
                Course Catalog Master
              </span>
              <span className="text-[10px] font-mono text-slate-500 font-bold">3 Active Courses</span>
            </div>

            <div className="space-y-2">
              {curriculum.map((course) => (
                <div
                  key={course.courseCode}
                  onClick={() => setSelectedCourseCode(course.courseCode)}
                  className={`p-3.5 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedCourseCode === course.courseCode
                      ? 'border-[#0B2644] bg-[#0B2644] text-white shadow-sm'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-black text-xs font-sc">{course.courseCode}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-black font-sc ${
                      selectedCourseCode === course.courseCode ? 'bg-amber-400 text-slate-900' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {course.programme.split(' ')[0]}
                    </span>
                  </div>
                  <div className="text-xs font-bold font-sc mt-1 truncate">{course.courseTitle}</div>
                  <div className={`text-[11px] mt-1 ${selectedCourseCode === course.courseCode ? 'text-slate-300' : 'text-slate-500'}`}>
                    Lead: {course.leadFaculty}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2 border-t border-slate-100 text-center">
              <span className="text-[11px] text-slate-500 font-mono">
                Least Privilege: Only Global Admin can modify course structure.
              </span>
            </div>
          </div>

          {/* Right: Modules & Component Slots Breakdown */}
          <div className="lg:col-span-8 bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b-2 border-slate-200">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                  Curriculum Hierarchy & Module Breakdown
                </span>
                <h3 className="text-lg font-black font-sc text-[#0B2644]">
                  {selectedCourse.courseCode}: {selectedCourse.courseTitle}
                </h3>
                <div className="text-xs text-slate-500 mt-0.5">
                  Target Cohort: <strong className="text-slate-700">{selectedCourse.targetCohort}</strong> • Assigned Faculty: <strong className="text-slate-700">{selectedCourse.leadFaculty}</strong>
                </div>
              </div>

              <div className="bg-emerald-50 text-emerald-800 text-xs font-mono font-bold px-3 py-1.5 rounded-xl border border-emerald-200">
                Audience: 6 Cleared Scholars
              </div>
            </div>

            <div className="space-y-4">
              {selectedCourse.modules.map((mod) => (
                <div key={mod.id} className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#7A470E] block">
                        UNIT {mod.number} • {mod.id}
                      </span>
                      <h4 className="text-sm font-bold font-sc text-slate-900">{mod.title}</h4>
                    </div>
                    <span className="text-[10px] bg-emerald-100 text-[#0B2644] font-black px-2 py-0.5 rounded font-sc">
                      {mod.status}
                    </span>
                  </div>

                  {/* Component Slots */}
                  <div className="space-y-1.5 pt-2 border-t border-slate-200">
                    <span className="text-[10px] uppercase font-bold text-slate-400 font-sc block">
                      Enforced LMS Component Slots:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {mod.components.map((comp, idx) => (
                        <div key={idx} className="p-2.5 rounded-lg bg-white border border-slate-200 text-xs flex items-center justify-between">
                          <div className="min-w-0 pr-2">
                            <span className="font-bold text-slate-800 block text-[11px] truncate font-sc">
                              {comp.label}
                            </span>
                            <span className="text-[10px] text-slate-500 font-mono block truncate">
                              {comp.file} ({comp.size})
                            </span>
                          </div>
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded shrink-0 font-sc ${
                            comp.status === 'Published' ? 'bg-emerald-100 text-[#7A470E]' : 'bg-amber-100 text-amber-900'
                          }`}>
                            {comp.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>

        </div>
      )}

      {/* TAB 2: FACULTY UPLOAD AUDIT QUEUE */}
      {activeTab === 'audit' && (
        <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between pb-4 border-b-2 border-slate-200">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                Quality Assurance & Routing Validation
              </span>
              <h3 className="text-lg font-black font-sc text-[#0B2644]">
                Faculty Material Upload Audit & Approval Desk
              </h3>
              <p className="text-xs text-slate-500">
                All learning materials uploaded by Faculty Chairs are verified against curriculum slots before student publication.
              </p>
            </div>
            <div className="text-xs font-mono bg-blue-50 text-[#0B2644] font-bold px-3 py-1.5 rounded-xl border border-blue-200">
              Taxonomy Guard Enforced
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-500 font-bold font-sc">
                  <th className="py-2.5 pr-2">Audit ID</th>
                  <th className="py-2.5 px-2">Submitting Faculty</th>
                  <th className="py-2.5 px-2">Course & Target Module</th>
                  <th className="py-2.5 px-2">Component Slot</th>
                  <th className="py-2.5 px-2">File & Payload</th>
                  <th className="py-2.5 px-2">Verified Audience</th>
                  <th className="py-2.5 px-2">Status</th>
                  <th className="py-2.5 pl-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {auditQueue.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="py-3 pr-2 font-mono text-[11px] font-bold text-slate-500">{item.id}</td>
                    <td className="py-3 px-2 font-bold text-slate-900">{item.faculty}</td>
                    <td className="py-3 px-2">
                      <span className="font-mono font-black text-[#0B2644] block font-sc">{item.course}</span>
                      <span className="text-[11px] text-slate-500">{item.module}</span>
                    </td>
                    <td className="py-3 px-2 font-bold text-slate-800 font-sc">{item.componentSlot}</td>
                    <td className="py-3 px-2 font-mono text-[11px] text-slate-600">
                      {item.fileName} <span className="text-slate-400">({item.size})</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-[10px] bg-slate-100 text-slate-800 font-bold px-2 py-0.5 rounded font-mono">
                        {item.verifiedAudience}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded font-sc ${
                        item.status.includes('Approved') 
                          ? 'bg-emerald-100 text-[#0B2644]' 
                          : 'bg-amber-100 text-[#7A470E]'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3 pl-2 text-right">
                      {item.status.includes('Pending') ? (
                        <button
                          onClick={() => handleApproveMaterial(item.id)}
                          className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg font-sc transition-colors cursor-pointer"
                        >
                          Clear & Publish
                        </button>
                      ) : (
                        <span className="text-slate-400 text-[11px] font-mono">Published</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: COHORT ACCESS & CLEARANCE GATES */}
      {activeTab === 'students' && (
        <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between pb-4 border-b-2 border-slate-200">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                Least Privilege Gatekeeper
              </span>
              <h3 className="text-lg font-black font-sc text-[#0B2644]">
                Student Cohort Access & Bursary Clearance Roster
              </h3>
              <p className="text-xs text-slate-500">
                LMS content is only routed to candidates whose financial and administrative clearance permits access.
              </p>
            </div>
            <div className="text-xs font-mono bg-emerald-50 text-emerald-800 font-bold px-3 py-1.5 rounded-xl border border-emerald-200">
              6 Verified Students Listed
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-500 font-bold font-sc">
                  <th className="py-2.5 pr-2">Scholar Name</th>
                  <th className="py-2.5 px-2">Matric / App No</th>
                  <th className="py-2.5 px-2">Programme</th>
                  <th className="py-2.5 px-2">Bursary Clearance Status</th>
                  <th className="py-2.5 px-2">Permitted LMS Scope</th>
                  <th className="py-2.5 pl-2 text-right">LMS Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {cohortAccessRoster.map((st, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="py-3 pr-2 font-bold text-slate-900">{st.name}</td>
                    <td className="py-3 px-2 font-mono text-[11px] text-slate-500">{st.matric}</td>
                    <td className="py-3 px-2 font-black text-[#0B2644] font-sc">{st.prog}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black font-sc ${
                        st.clearance.includes('Paid') || st.clearance.includes('Cleared')
                          ? 'bg-emerald-100 text-[#0B2644]'
                          : 'bg-amber-100 text-amber-900'
                      }`}>
                        {st.clearance}
                      </span>
                    </td>
                    <td className="py-3 px-2 font-medium text-slate-700">{st.accessScope}</td>
                    <td className="py-3 pl-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black font-sc ${
                        st.status === 'Active' ? 'bg-emerald-100 text-[#7A470E]' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {st.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: LEAST PRIVILEGE & RBAC SECURITY MATRIX */}
      {activeTab === 'security' && (
        <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-sm space-y-6">
          <div>
            <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
              Access Governance
            </span>
            <h3 className="text-lg font-black font-sc text-[#0B2644]">
              AKSU Business School Role-Based Access Control (RBAC) & Least Privilege Policy
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Every persona in the LMS is provisioned with minimum necessary credentials to perform their duties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs">
            <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-4 h-4 text-[#0B2644]" />
                <span className="font-bold text-slate-900 font-sc text-sm">Tier 1: Global Admin (Director)</span>
              </div>
              <p className="text-slate-600 text-[11px]">
                Full read and approval authority over university financial settlements, Paystack receipts, Doctoral defences, and Senate reports. Cannot be edited by lower tiers.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <span className="font-bold text-slate-900 font-sc text-sm">Tier 1: Global LMS Admin (Content Manager)</span>
              </div>
              <p className="text-slate-600 text-[11px]">
                Global write/publish authority over curricula, module slots, faculty allocations, and quality audit clearing. Configures permissions for all faculties and cohorts.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4 text-[#7A470E]" />
                <span className="font-bold text-slate-900 font-sc text-sm">Tier 2: Scoped Faculty Chair</span>
              </div>
              <p className="text-slate-600 text-[11px]">
                Least privilege restricts faculty to upload materials <strong>only</strong> to specifically assigned courses (e.g. DBA 911). Cannot alter other departments' syllabus or access university treasury data.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200 space-y-2">
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4 text-blue-700" />
                <span className="font-bold text-slate-900 font-sc text-sm">Tier 3: Enrolled Scholar</span>
              </div>
              <p className="text-slate-600 text-[11px]">
                Restricted strictly to the syllabus and cleared materials for their specific registered programme and cohort. Cannot view faculty grading dockets or unapproved drafts.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
